<script setup lang="ts">
/**
 * InfoHideMockup — 信息隐藏 mockup（纯 CSS mockup 直出，不再请求后端截图）
 *
 * v5 真实能力（hide.js + tools/rules/rules.json）：
 *   - 浏览器 TITLE 被替换
 *   - 视频播放器（16:9 黑底占位）
 *   - 主标题 + 简介被替换
 *   - 评论区被替换
 *   - 右侧推荐视频列表（封面+标题被替换）
 */
import YfbIcon from '@/components/YfbIcon.vue'

interface RecommendedVideo {
  platform: string
  platformName: string
  uploadedAgo: string
}

const recommended: RecommendedVideo[] = [
  { platform: 'cctv', platformName: '央视', uploadedAgo: '3 天前' },
  { platform: 'migu', platformName: '咪咕', uploadedAgo: '1 周前' },
  { platform: 'tencent', platformName: '腾讯', uploadedAgo: '2 周前' }
]

const hiddenCount = 8
</script>

<template>
  <div class="ih-mock">
        <!-- 浏览器 chrome 栏 -->
        <div class="ih-mock__chrome">
          <div class="ih-mock__chrome-dots">
            <span class="ih-mock__dot"></span>
            <span class="ih-mock__dot"></span>
            <span class="ih-mock__dot"></span>
          </div>
          <div class="ih-mock__chrome-title">
            <span class="ih-mock__chrome-title-label">TITLE:</span>
            <span class="ih-mock__chrome-title-value">[隐分宝：网站标题]</span>
          </div>
          <div class="ih-mock__chrome-url">
            <span class="ih-mock__chrome-url-lock" aria-hidden="true">⚿</span>
            https://miguvideo.com/replay/123
          </div>
        </div>

    <!-- 已隐藏 N 处 角标 -->
    <div class="ih-mock__badge">
      <YfbIcon name="lucide:shield-check" :size="11" />
      <span>已隐藏 {{ hiddenCount }} 处剧透元素</span>
    </div>

    <!-- 主体：左侧主区 + 右侧推荐列表 -->
    <div class="ih-mock__body">
      <!-- 左侧主区 -->
      <div class="ih-mock__main">
        <!-- 视频播放器 -->
        <div class="ih-mock__player">
          <div class="ih-mock__player-frame">
            <div class="ih-mock__player-play">▶</div>
            <div class="ih-mock__player-label">[隐分宝：视频]</div>
          </div>
          <div class="ih-mock__player-meta">
            <span class="ih-mock__player-progress">
              <span class="ih-mock__player-progress-fill"></span>
            </span>
          </div>
        </div>

        <!-- 主标题 + 简介 -->
        <div class="ih-mock__title-block">
          <h3 class="ih-mock__title">[隐分宝：主标题]</h3>
          <div class="ih-mock__meta-row">
            <span class="ih-mock__meta-chip">[隐分宝：联赛]</span>
            <span class="ih-mock__meta-chip">[隐分宝：日期]</span>
            <span class="ih-mock__meta-chip">[隐分宝：解说]</span>
          </div>
          <div class="ih-mock__desc">
            <div class="ih-mock__desc-line">[隐分宝：简介内容]</div>
            <div class="ih-mock__desc-line">[隐分宝：简介内容]</div>
            <div class="ih-mock__desc-line ih-mock__desc-line--short">[隐分宝：简介]</div>
          </div>
        </div>

        <!-- 评论区 -->
        <div class="ih-mock__comments">
          <div class="ih-mock__comments-head">
            <span class="ih-mock__comments-count">💬 [隐分宝：评论数]</span>
            <span class="ih-mock__comments-sort">最新 ▾</span>
          </div>
          <div class="ih-mock__comment-list">
            <div v-for="i in 3" :key="i" class="ih-mock__comment">
              <span class="ih-mock__comment-avatar"></span>
              <div class="ih-mock__comment-body">
                <div class="ih-mock__comment-name">[隐分宝：用户名]</div>
                <div class="ih-mock__comment-text">[隐分宝：评论内容]</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧推荐视频列表 -->
      <aside class="ih-mock__side">
        <div class="ih-mock__side-title">相关推荐</div>
        <div
          v-for="(r, idx) in recommended"
          :key="idx"
          class="ih-mock__rec"
        >
          <div class="ih-mock__rec-thumb">
            <div class="ih-mock__rec-thumb-grid"></div>
            <div class="ih-mock__rec-thumb-placeholder">[隐分宝：封面]</div>
          </div>
          <div class="ih-mock__rec-meta">
            <div class="ih-mock__rec-title">[隐分宝：标题]</div>
            <div class="ih-mock__rec-sub">
              <span>{{ r.platformName }}</span>
              <span>·</span>
              <span>{{ r.uploadedAgo }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.ih-mock {
  border: var(--yfb-border-3) solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  overflow: hidden;
}

/* 浏览器 chrome */
.ih-mock__chrome {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  background-color: var(--yfb-surface-2);
  border-bottom: var(--yfb-border-2) solid var(--yfb-rule-soft);
  flex-wrap: wrap;
}

.ih-mock__chrome-dots {
  display: flex;
  gap: var(--yfb-sp-1);
}

.ih-mock__dot {
  display: block;
  width: 8px;
  height: 8px;
  background-color: var(--yfb-ink-soft);
  border-radius: 50%;
}

.ih-mock__chrome-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  min-width: 0;
}

.ih-mock__chrome-title-label {
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
  color: var(--yfb-text-secondary);
  flex-shrink: 0;
}

.ih-mock__chrome-title-value {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1.5px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ih-mock__chrome-url {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-surface);
  color: var(--yfb-ink);
  border: 1.5px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.ih-mock__chrome-url-lock {
  color: var(--yfb-accent);
  font-size: 11px;
}

/* 角标 */
.ih-mock__badge {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  margin: 12px;
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  box-shadow: var(--yfb-shadow-hit);
}

/* 主体 */
.ih-mock__body {
  display: grid;
  grid-template-columns: 1fr 200px;
  gap: var(--yfb-sp-3);
  padding: 0 12px 12px;
}

.ih-mock__main {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  min-width: 0;
}

/* 视频播放器 */
.ih-mock__player {
  display: flex;
  flex-direction: column;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
}

.ih-mock__player-frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: var(--yfb-panel-bg);
  border: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.ih-mock__player-frame::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: var(--yfb-radius-md);
  background-image:
    repeating-linear-gradient(0deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px),
    repeating-linear-gradient(90deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px);
}

.ih-mock__player-play {
  position: relative;
  width: 56px;
  height: 56px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border-radius: var(--yfb-radius-md);
  box-shadow: var(--yfb-shadow-hit);
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.ih-mock__player-label {
  position: relative;
  padding: var(--yfb-sp-1) var(--yfb-sp-3);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border: 1.5px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
  z-index: 1;
}

.ih-mock__player-meta {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: 0 4px;
}

.ih-mock__player-time {
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-ink);
  letter-spacing: var(--yfb-ls-mono);
}

.ih-mock__player-progress {
  flex: 1;
  height: 4px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  position: relative;
  overflow: hidden;
}

.ih-mock__player-progress-fill {
  position: absolute;
  inset: 0;
  width: 30%;
  background-color: var(--yfb-signal-danger);
  border-radius: var(--yfb-radius-sm);
}

/* 标题 + 简介 */
.ih-mock__title-block {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-3);
  background-color: var(--yfb-surface);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-left: var(--yfb-border-3) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.ih-mock__title {
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  margin: 0;
  color: var(--yfb-on-accent);
  background-color: var(--yfb-accent);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1.5px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-button);
  display: inline-block;
  align-self: flex-start;
  max-width: 100%;
}

.ih-mock__meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-1);
}

.ih-mock__meta-chip {
  padding: 2px 6px;
  background-color: var(--yfb-surface-2);
  color: var(--yfb-text-secondary);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
}

.ih-mock__desc {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-1);
}

.ih-mock__desc-line {
  height: 10px;
  background-color: var(--yfb-surface-2);
  border-left: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
}

.ih-mock__desc-line--short {
  width: 60%;
}

/* 评论 */
.ih-mock__comments {
  padding: var(--yfb-sp-3);
  background-color: var(--yfb-surface-2);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.ih-mock__comments-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: var(--yfb-border-2) solid var(--yfb-rule-soft);
}

.ih-mock__comments-count {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-ink);
}

.ih-mock__comments-count::before {
  content: "";
  display: inline-block;
  padding: 2px 6px;
  background-color: var(--yfb-accent);
  border-radius: var(--yfb-radius-sm);
  margin-right: 6px;
}

.ih-mock__comments-sort {
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-transform: uppercase;
}

.ih-mock__comment-list {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
}

.ih-mock__comment {
  display: flex;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2);
  background-color: var(--yfb-surface);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.ih-mock__comment-avatar {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: var(--yfb-accent);
  border: 1.5px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

.ih-mock__comment-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-1);
}

.ih-mock__comment-name {
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--yfb-on-accent);
  background-color: var(--yfb-accent);
  padding: 1px 6px;
  border: 1.5px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
  align-self: flex-start;
}

.ih-mock__comment-text {
  height: 8px;
  background-color: var(--yfb-surface-2);
  border-left: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
}

/* 右侧推荐 */
.ih-mock__side {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
  min-width: 0;
}

.ih-mock__side-title {
  padding: var(--yfb-sp-2) var(--yfb-sp-2);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
}

.ih-mock__rec {
  display: flex;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2);
  background-color: var(--yfb-surface);
  border: var(--yfb-border-2) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.ih-mock__rec-thumb {
  flex-shrink: 0;
  position: relative;
  width: 64px;
  height: 40px;
  background-color: var(--yfb-panel-bg);
  border: 1.5px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.ih-mock__rec-thumb-grid {
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, transparent 0, transparent 6px, var(--yfb-grid-line) 6px, var(--yfb-grid-line) 7px),
    repeating-linear-gradient(90deg, transparent 0, transparent 6px, var(--yfb-grid-line) 6px, var(--yfb-grid-line) 7px);
}

.ih-mock__rec-thumb-placeholder {
  position: relative;
  font-family: var(--yfb-mono);
  font-size: 7px;
  font-weight: 700;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  padding: 2px 4px;
  border: 1px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
  text-align: center;
  z-index: 1;
}

.ih-mock__rec-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-1);
  justify-content: center;
}

.ih-mock__rec-title {
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--yfb-on-accent);
  background-color: var(--yfb-accent);
  padding: 2px 6px;
  border: 1.5px dashed var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
  line-height: 1.3;
}

.ih-mock__rec-sub {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-1);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  letter-spacing: var(--yfb-ls-mono);
}

@media (max-width: 767px) {
  .ih-mock__body {
    grid-template-columns: 1fr;
  }

  .ih-mock__chrome {
    flex-direction: column;
    align-items: flex-start;
    gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  }

  .ih-mock__chrome-title {
    width: 100%;
  }
}
</style>

